import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import VendrLayout from '../VendrLayout';
import { useClientProfile } from '../context/ClientProfileContext';
import { profileToFormState } from '../utils/profileForm';
import './VendrSetup.css';

const VendrSetup = () => {
  const { profile, loading, saveProfile, isSetupComplete, emptyProfile } = useClientProfile();
  const navigate = useNavigate();
  const [form, setForm] = useState(emptyProfile);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    const next = profileToFormState(profile);
    if (next) setForm(next);
  }, [profile]);

  useEffect(() => {
    if (!loading && isSetupComplete) {
      navigate('/vendr', { replace: true });
    }
  }, [loading, isSetupComplete, navigate]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    if (type === 'checkbox') {
      setForm((prev) => {
        const next = { ...prev, [name]: checked };
        if (name === 'ship_from_same_as_address' && checked) {
          next.ship_from_address_line_1 = prev.address_line_1;
          next.ship_from_address_line_2 = prev.address_line_2 ?? '';
          next.ship_from_address_line_3 = prev.address_line_3 ?? '';
          next.ship_from_town_city = prev.town_city;
          next.ship_from_postcode = prev.postcode;
          next.ship_from_country = prev.country;
        }
        return next;
      });
      return;
    }
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSaving(true);
    const payload = { ...form };
    if (payload.ship_from_same_as_address) {
      payload.ship_from_address_line_1 = null;
      payload.ship_from_address_line_2 = null;
      payload.ship_from_address_line_3 = null;
      payload.ship_from_town_city = null;
      payload.ship_from_postcode = null;
      payload.ship_from_country = null;
    }
    const result = await saveProfile(payload);
    setSaving(false);
    if (result.success) {
      navigate('/vendr');
    } else {
      setError(result.error || 'Failed to save');
    }
  };

  if (loading) {
    return (
      <VendrLayout>
        <div className="vendr-setup-loading">Loading...</div>
      </VendrLayout>
    );
  }

  return (
    <VendrLayout>
      <div className="vendr-setup">
        <h1 className="vendr-setup-title">Company setup</h1>
        <p className="vendr-setup-intro">
          Complete your company and contact details. You only need to do this once.
        </p>

        <form onSubmit={handleSubmit} className="vendr-setup-form">
          {error && <div className="vendr-setup-error">{error}</div>}

          <section className="vendr-setup-section">
            <h2>Primary contact details</h2>
            <div className="vendr-setup-grid">
              <div className="vendr-setup-field">
                <label htmlFor="first_name">First name *</label>
                <input
                  id="first_name"
                  name="first_name"
                  value={form.first_name}
                  onChange={handleChange}
                  required
                />
              </div>
              <div className="vendr-setup-field">
                <label htmlFor="last_name">Last name *</label>
                <input
                  id="last_name"
                  name="last_name"
                  value={form.last_name}
                  onChange={handleChange}
                  required
                />
              </div>
              <div className="vendr-setup-field">
                <label htmlFor="job_title">Job title *</label>
                <input
                  id="job_title"
                  name="job_title"
                  value={form.job_title}
                  onChange={handleChange}
                  required
                />
              </div>
              <div className="vendr-setup-field">
                <label htmlFor="company_name">Company name *</label>
                <input
                  id="company_name"
                  name="company_name"
                  value={form.company_name}
                  onChange={handleChange}
                  required
                />
              </div>
              <div className="vendr-setup-field">
                <label htmlFor="email">Email *</label>
                <input
                  id="email"
                  name="email"
                  type="email"
                  value={form.email}
                  onChange={handleChange}
                  required
                />
              </div>
              <div className="vendr-setup-field">
                <label htmlFor="mobile_phone">Mobile phone *</label>
                <input
                  id="mobile_phone"
                  name="mobile_phone"
                  type="tel"
                  value={form.mobile_phone}
                  onChange={handleChange}
                  required
                />
              </div>
              <div className="vendr-setup-field vendr-setup-field-full">
                <label htmlFor="secondary_phone">Secondary phone</label>
                <input
                  id="secondary_phone"
                  name="secondary_phone"
                  type="tel"
                  value={form.secondary_phone}
                  onChange={handleChange}
                />
              </div>
            </div>
          </section>

          <section className="vendr-setup-section">
            <h2>Company / supplier details</h2>
            <div className="vendr-setup-grid">
              <div className="vendr-setup-field vendr-setup-field-full">
                <label htmlFor="brand_supplier_name">Brand / supplier name *</label>
                <input
                  id="brand_supplier_name"
                  name="brand_supplier_name"
                  value={form.brand_supplier_name}
                  onChange={handleChange}
                  required
                />
              </div>
              <div className="vendr-setup-field vendr-setup-field-full">
                <label htmlFor="address_line_1">Address line 1 *</label>
                <input
                  id="address_line_1"
                  name="address_line_1"
                  value={form.address_line_1}
                  onChange={handleChange}
                  required
                />
              </div>
              <div className="vendr-setup-field vendr-setup-field-full">
                <label htmlFor="address_line_2">Address line 2</label>
                <input
                  id="address_line_2"
                  name="address_line_2"
                  value={form.address_line_2}
                  onChange={handleChange}
                />
              </div>
              <div className="vendr-setup-field vendr-setup-field-full">
                <label htmlFor="address_line_3">Address line 3</label>
                <input
                  id="address_line_3"
                  name="address_line_3"
                  value={form.address_line_3}
                  onChange={handleChange}
                />
              </div>
              <div className="vendr-setup-field">
                <label htmlFor="town_city">Town / city *</label>
                <input
                  id="town_city"
                  name="town_city"
                  value={form.town_city}
                  onChange={handleChange}
                  required
                />
              </div>
              <div className="vendr-setup-field">
                <label htmlFor="postcode">Postcode *</label>
                <input
                  id="postcode"
                  name="postcode"
                  value={form.postcode}
                  onChange={handleChange}
                  required
                />
              </div>
              <div className="vendr-setup-field">
                <label htmlFor="country">Country *</label>
                <input
                  id="country"
                  name="country"
                  value={form.country}
                  onChange={handleChange}
                  required
                />
              </div>
            </div>
          </section>

          <section className="vendr-setup-section vendr-setup-section-ship-from">
            <h2>Ship from address</h2>
            <div className="vendr-setup-same-as-wrap">
              <label className="vendr-setup-checkbox-label">
                <input
                  type="checkbox"
                  name="ship_from_same_as_address"
                  checked={!!form.ship_from_same_as_address}
                  onChange={handleChange}
                />
                <span>Same as company address</span>
              </label>
            </div>
            {!form.ship_from_same_as_address && (
              <div className="vendr-setup-grid">
                <div className="vendr-setup-field vendr-setup-field-full">
                  <label htmlFor="ship_from_address_line_1">Address line 1 *</label>
                  <input
                    id="ship_from_address_line_1"
                    name="ship_from_address_line_1"
                    value={form.ship_from_address_line_1 ?? ''}
                    onChange={handleChange}
                    required={!form.ship_from_same_as_address}
                  />
                </div>
                <div className="vendr-setup-field vendr-setup-field-full">
                  <label htmlFor="ship_from_address_line_2">Address line 2</label>
                  <input
                    id="ship_from_address_line_2"
                    name="ship_from_address_line_2"
                    value={form.ship_from_address_line_2 ?? ''}
                    onChange={handleChange}
                  />
                </div>
                <div className="vendr-setup-field vendr-setup-field-full">
                  <label htmlFor="ship_from_address_line_3">Address line 3</label>
                  <input
                    id="ship_from_address_line_3"
                    name="ship_from_address_line_3"
                    value={form.ship_from_address_line_3 ?? ''}
                    onChange={handleChange}
                  />
                </div>
                <div className="vendr-setup-field">
                  <label htmlFor="ship_from_town_city">Town / city *</label>
                  <input
                    id="ship_from_town_city"
                    name="ship_from_town_city"
                    value={form.ship_from_town_city ?? ''}
                    onChange={handleChange}
                    required={!form.ship_from_same_as_address}
                  />
                </div>
                <div className="vendr-setup-field">
                  <label htmlFor="ship_from_postcode">Postcode *</label>
                  <input
                    id="ship_from_postcode"
                    name="ship_from_postcode"
                    value={form.ship_from_postcode ?? ''}
                    onChange={handleChange}
                    required={!form.ship_from_same_as_address}
                  />
                </div>
                <div className="vendr-setup-field">
                  <label htmlFor="ship_from_country">Country *</label>
                  <input
                    id="ship_from_country"
                    name="ship_from_country"
                    value={form.ship_from_country ?? ''}
                    onChange={handleChange}
                    required={!form.ship_from_same_as_address}
                  />
                </div>
              </div>
            )}
          </section>

          <div className="vendr-setup-actions">
            <button type="submit" className="vendr-setup-submit" disabled={saving}>
              {saving ? 'Saving...' : 'Save and continue'}
            </button>
          </div>
        </form>
      </div>
    </VendrLayout>
  );
};

export default VendrSetup;
